import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { WebSocketContext } from '../websocket-context/websocket-provider';

import {
  buildStrategyDraft,
  cloneBacktestingValue,
  formatTradeStrategyLabel,
  generateBacktestLabel,
  generateCopyStrategyName,
  getDuplicateStrategyIds,
} from './backtesting-provider.helpers';
import { buildRunBacktestRequest, BACKTESTER_CONFIG_KEYS } from './api/backtester-config-api';
import {
  FetchBacktesterConfigResult,
  BacktesterRunResponse,
  BacktesterScenario,
  BacktesterStrategyData,
  BacktesterStratlet,
  ComparisonOp,
} from './api/backtester-config-api.types';
import {
  BacktestHeaderErrors,
  BacktestHeaderField,
  BacktestHeaderRegistryEntry,
  BacktestGlobalConfig,
  BacktestTab,
  createDefaultGlobalConfig,
  DEFAULT_BACKTEST_EXECUTION_STATE,
  IBacktestingProvider,
  IBacktestingProviderProps,
  StrategyFormRegistryEntry,
  StrategyFormData,
  StrategyTab,
  StrategyTemplate,
} from './types';
import { useBacktesterConfigs } from './hooks/use-backtester-configs';
import { AllToggles, createStrategySchema } from './validation/schemas/strategy';
import {
  toastBacktestRunError,
  toastConfigDeleted,
  toastConfigDeleteError,
  toastConfigSaved,
  toastConfigSaveError,
  toastConfigUpdated,
  toastConfigUpdateError,
} from './backtesting-toasts';

export const SKIP_VALIDATION_FOR_DEMO = false;

let backtestIdCounter = 0;
const generateBacktestId = () => `backtest-${++backtestIdCounter}-${Date.now()}`;

let strategyIdCounter = 0;
const generateStrategyId = () => `strategy-${++strategyIdCounter}-${Date.now()}`;

const createDefaultBacktestExecutionState = () => ({
  ...DEFAULT_BACKTEST_EXECUTION_STATE,
  rawResult: null,
  strategyData: null,
  strategyNames: [],
});

// The schema's conditional fields are gated by these toggles (same mapping the
// live form derives in use-strategy-form) — needed to validate a stored draft
// directly when its form isn't mounted (e.g. Run from the run-prompt).
const togglesFromDraft = (draft: StrategyFormData): AllToggles => ({
  includePerpetualSpread: draft.execution.includePerpetualSpread,
  includeVolSpread: draft.execution.includeVolSpread,
  includePerpetualFee: draft.execution.includePerpetualFee,
  includeOptionsFee: draft.execution.includeOptionsFee,
  whenExecutionWeekdays: draft.execution.whenExecutionWeekdays,
  whenExecutionAverageIn: draft.execution.whenExecutionAverageIn,
  optionsNotionalScaled: draft.execution.optionsNotionalScaled,
  deltaThreshold: draft.hedging.deltaThreshold,
  deltaThresholdUpper: draft.hedging.deltaThresholdUpper,
  deltaThresholdLower: draft.hedging.deltaThresholdLower,
  deltaTarget: draft.hedging.deltaTarget,
});

const isStrategyDraftValid = (draft: StrategyFormData): boolean =>
  createStrategySchema(togglesFromDraft(draft)).safeParse(draft).success;

const validateGlobalConfig = (globalConfig: BacktestGlobalConfig): BacktestHeaderErrors => {
  const errors: BacktestHeaderErrors = {};
  if (!globalConfig.token) errors.token = 'Token is required';
  const { start, end } = globalConfig.backtestPeriod;
  if (!start || !end) errors.backtestPeriod = 'Backtest period is required';
  else if (start > end) errors.backtestPeriod = 'Start date must be before end date';
  if (!globalConfig.frequency || globalConfig.frequency <= 0) errors.frequency = 'Required';
  return errors;
};

const createDefaultBacktestTab = (label: string): BacktestTab => ({
  id: generateBacktestId(),
  label,
  globalConfig: createDefaultGlobalConfig(),
  headerErrors: {},
  strategies: [],
  activeStrategyId: null,
  execution: createDefaultBacktestExecutionState(),
});

// The placeholder label a fresh study carries until it's named/saved. Not a
// meaningful library key, so auto-save falls back to the first backtest's name.
const PLACEHOLDER_STUDY_LABEL = 'Untitled study';

// Resolve the library name to auto-save a study under on run. Prefer a
// user-meaningful study label; fall back to the first backtest's name; then
// uniquify against existing entries so a fresh draft never silently overwrites
// an unrelated saved study that happens to share the default name.
const resolveAutoSaveName = (
  studyLabel: string,
  firstBacktestName: string | undefined,
  existingNames: Set<string>,
): string => {
  const trimmedLabel = studyLabel.trim();
  const base =
    trimmedLabel && trimmedLabel !== PLACEHOLDER_STUDY_LABEL
      ? trimmedLabel
      : firstBacktestName?.trim() || PLACEHOLDER_STUDY_LABEL;

  if (!existingNames.has(base)) return base;
  let suffix = 2;
  while (existingNames.has(`${base} (${suffix})`)) suffix += 1;
  return `${base} (${suffix})`;
};

const DEFAULT_REPORTING = {
  frequency: 1440,
  exclude_midnight: false,
  risk_report: false,
  blotter_report: false,
} as const;

const WEEKDAY_TO_API: Record<string, string> = {
  monday: 'MON',
  tuesday: 'TUE',
  wednesday: 'WED',
  thursday: 'THU',
  friday: 'FRI',
  saturday: 'SAT',
  sunday: 'SUN',
};

const COMPARISON_TO_API: Record<string, ComparisonOp> = {
  gt: '>',
  gte: '>=',
  lt: '<',
  lte: '<=',
  eq: '==',
  neq: '!=',
};

const API_TO_COMPARISON: Record<ComparisonOp, StrategyFormData['entryConditions']['comparison']> = {
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  '==': 'eq',
  '!=': 'neq',
};

const formatBacktesterDate = (date: Date | null) => {
  if (!date) return '';

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const parseNumberValue = (value: string, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const stringifyFormValue = (value: string | number | null | undefined) => {
  if (value == null) return '';
  return String(value);
};

const parseBacktesterDateString = (value: string | null | undefined) => {
  if (!value) return null;

  const normalizedValue = value.includes('T') ? value : value.replace(' ', 'T');
  const parsed = new Date(normalizedValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const normalizeApiWeekdays = (value: string | undefined) =>
  (value ?? '')
    .split(',')
    .map((day) => day.trim().toUpperCase())
    .filter(Boolean);

const API_WEEKDAY_TO_FORM: Record<string, string> = {
  MON: 'monday',
  TUE: 'tuesday',
  WED: 'wednesday',
  THU: 'thursday',
  FRI: 'friday',
};

const DEFAULT_AVERAGE_IN_WEEKDAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
const DEFAULT_AVERAGE_IN_ALL_DAYS = [...DEFAULT_AVERAGE_IN_WEEKDAYS, 'SAT', 'SUN'];

const hasSameValues = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const mapExecutionFromApi = (
  execution: BacktesterStratlet['execution'],
): StrategyFormData['execution'] => {
  const weekdays = normalizeApiWeekdays(execution.weekdays);
  const isAverageInWeekdays = hasSameValues(weekdays, DEFAULT_AVERAGE_IN_WEEKDAYS);
  const isAverageInAllDays = hasSameValues(weekdays, DEFAULT_AVERAGE_IN_ALL_DAYS);
  const shouldUseAverageIn = isAverageInWeekdays || isAverageInAllDays;

  return {
    executionTime: execution.execution_time ?? '',
    whenExecutionWeekdays: !shouldUseAverageIn && weekdays.length > 0,
    whenExecutionWeekdaysDays: shouldUseAverageIn
      ? []
      : weekdays
          .map((day) => API_WEEKDAY_TO_FORM[day])
          .filter((day): day is string => Boolean(day)),
    whenExecutionWeekdaysTime: '',
    whenExecutionAverageIn: shouldUseAverageIn,
    // Prefer the explicit include_weekends flag; fall back to the weekdays-list
    // encoding (MON..SUN) for older saved configs that predate the flag.
    whenExecutionAverageInIncludeWeekends:
      execution.include_weekends != null ? Boolean(execution.include_weekends) : isAverageInAllDays,
    optionsNotionalScaled: Boolean(execution.notional_scaling),
    notionalScalingType: execution.notional_scaling ? (execution.notional_scaling_type ?? '') : '',
    includePerpetualSpread: execution.perp_spread !== 0,
    perpetualSpread: stringifyFormValue(execution.perp_spread),
    includeVolSpread: execution.vol_spread !== 0,
    volSpread: stringifyFormValue(execution.vol_spread),
    includePerpetualFee: execution.perp_fee !== 0,
    perpetualFee: stringifyFormValue(execution.perp_fee),
    includeOptionsFee: execution.options_fee !== 0,
    optionsFee: stringifyFormValue(execution.options_fee),
    convertPerpCashflowToUsd: Boolean(execution.convert_perp_cashflows_to_usd),
    optionsSettleInUsd: Boolean(execution.options_settle_in_usd),
    convertFundingCashflowsToUsd: Boolean(execution.convert_funding_cashflows_to_usd),
    closeStructureOnFirstExpiry: Boolean(execution.close_structure_on_first_expiry),
    executeTradesOnlyOnce: Boolean(execution.once_only),
    useExchangeStrikes: Boolean(execution.use_exchange_strikes),
    exchangeTimeProjection: execution.exchange_projection?.time_projection ?? 'weighted_time',
    exchangeStrikeProjection: execution.exchange_projection?.strike_projection ?? 'weighted_strike',
  };
};

const mapEntryConditionsFromApi = (
  entryConditions: BacktesterStratlet['entry_conditions'],
): StrategyFormData['entryConditions'] => {
  const firstCondition = entryConditions?.[0];
  const [token = 'BTC', instrument = '', tenor = ''] = firstCondition?.expression?.field
    ?.split('/')
    ?.map((value) => value.trim()) ?? ['BTC', '', ''];

  return {
    enabled: Boolean(firstCondition),
    category: firstCondition?.category?.toLowerCase() ?? 'market',
    token,
    instrument,
    tenor,
    comparison: firstCondition ? (API_TO_COMPARISON[firstCondition.comparison] ?? 'gt') : 'gt',
    value: stringifyFormValue(firstCondition?.value),
  };
};

const mapStratletToFormData = (stratlet: BacktesterStratlet): StrategyFormData =>
  buildStrategyDraft({
    name: stratlet.name?.trim() || 'Trade Strategy',
    accountInformation: {
      token: stratlet.account.token || 'BTC',
      tokenBalance: stringifyFormValue(stratlet.account.token_balance),
      usdBalance: stringifyFormValue(stratlet.account.usd_balance),
      includeInitialPerpetual: stratlet.account.perpetual !== 0,
      initialPerpetualBalance: stringifyFormValue(stratlet.account.perpetual),
    },
    execution: mapExecutionFromApi(stratlet.execution),
    hedging: {
      frequency: stringifyFormValue(stratlet.hedging.frequency),
      includeFundingRates: Boolean(stratlet.hedging.include_funding_rates),
      deltaThreshold: stratlet.hedging.delta_threshold != null,
      deltaThresholdValue: stringifyFormValue(stratlet.hedging.delta_threshold),
      deltaThresholdUpper: stratlet.hedging.delta_threshold_upper != null,
      deltaThresholdUpperValue: stringifyFormValue(stratlet.hedging.delta_threshold_upper),
      deltaThresholdLower: stratlet.hedging.delta_threshold_lower != null,
      deltaThresholdLowerValue: stringifyFormValue(stratlet.hedging.delta_threshold_lower),
      deltaTarget: stratlet.hedging.delta_target != null,
      deltaTargetValue: stringifyFormValue(stratlet.hedging.delta_target),
      includeTokenBalanceInDelta: Boolean(stratlet.hedging.include_token_balance_in_delta),
    },
    portfolio: {
      instruments:
        stratlet.portfolio.length > 0
          ? stratlet.portfolio.map((instrument) => ({
              tenor: instrument.tenor,
              notional: stringifyFormValue(instrument.notional),
              deltaOrStrike:
                'delta' in instrument ? instrument.delta : stringifyFormValue(instrument.strike),
            }))
          : [{ tenor: '', notional: '', deltaOrStrike: '' }],
    },
    entryConditions: mapEntryConditionsFromApi(stratlet.entry_conditions),
  });

const mapWeekdaysToApi = (data: StrategyFormData['execution']) => {
  if (data.whenExecutionWeekdays && data.whenExecutionWeekdaysDays.length > 0) {
    return data.whenExecutionWeekdaysDays
      .map((day) => WEEKDAY_TO_API[day] ?? day.slice(0, 3).toUpperCase())
      .join(',');
  }

  return data.whenExecutionAverageInIncludeWeekends
    ? 'MON,TUE,WED,THU,FRI,SAT,SUN'
    : 'MON,TUE,WED,THU,FRI';
};

const mapInstrumentToApi = (instrument: StrategyFormData['portfolio']['instruments'][number]) => {
  const deltaOrStrike = instrument.deltaOrStrike.trim();
  const base = {
    tenor: instrument.tenor,
    notional: parseNumberValue(instrument.notional),
  };

  if (/[PC]$/i.test(deltaOrStrike)) {
    return {
      ...base,
      delta: deltaOrStrike.toUpperCase(),
    };
  }

  return {
    ...base,
    strike: deltaOrStrike,
  };
};

const mapEntryConditionsToApi = (data: StrategyFormData['entryConditions']) => {
  if (!data.enabled) return undefined;

  return [
    {
      category: data.category.toUpperCase(),
      expression: {
        field: `${data.token}/${data.instrument}/${data.tenor}`,
      },
      comparison: COMPARISON_TO_API[data.comparison] ?? '>',
      value: parseNumberValue(data.value),
    },
  ];
};

const mapStrategyFormToApi = (data: StrategyFormData): BacktesterStratlet => ({
  name: data.name.trim(),
  account: {
    token: data.accountInformation.token,
    token_balance: parseNumberValue(data.accountInformation.tokenBalance),
    usd_balance: parseNumberValue(data.accountInformation.usdBalance),
    perpetual: data.accountInformation.includeInitialPerpetual
      ? parseNumberValue(data.accountInformation.initialPerpetualBalance)
      : 0,
  },
  execution: {
    perp_spread: data.execution.includePerpetualSpread
      ? parseNumberValue(data.execution.perpetualSpread)
      : 0,
    vol_spread: data.execution.includeVolSpread ? parseNumberValue(data.execution.volSpread) : 0,
    perp_fee: data.execution.includePerpetualFee
      ? parseNumberValue(data.execution.perpetualFee)
      : 0,
    options_fee: data.execution.includeOptionsFee ? parseNumberValue(data.execution.optionsFee) : 0,
    execution_style: data.execution.whenExecutionWeekdays ? 'WEEKDAYS' : 'AVERAGE_IN',
    execution_time: data.execution.executionTime,
    notional_scaling: data.execution.optionsNotionalScaled,
    notional_scaling_type: data.execution.notionalScalingType,
    options_settle_in_usd: data.execution.optionsSettleInUsd,
    weekdays: mapWeekdaysToApi(data.execution),
    include_weekends: data.execution.whenExecutionAverageInIncludeWeekends,
    convert_perp_cashflows_to_usd: data.execution.convertPerpCashflowToUsd,
    convert_funding_cashflows_to_usd: data.execution.convertFundingCashflowsToUsd,
    close_structure_on_first_expiry: data.execution.closeStructureOnFirstExpiry,
    once_only: data.execution.executeTradesOnlyOnce,
    use_exchange_strikes: data.execution.useExchangeStrikes,
    // Venue + projection only when exchange strikes are on; off, we send just
    // the flag and let the engine apply its own defaults (matches how the other
    // optional groups, e.g. entry_conditions, are spread in only when present).
    ...(data.execution.useExchangeStrikes
      ? {
          exchange_strike_venue: 'DERIBIT',
          exchange_projection: {
            time_projection: data.execution.exchangeTimeProjection,
            strike_projection: data.execution.exchangeStrikeProjection,
          },
        }
      : {}),
  },
  hedging: {
    frequency: parseNumberValue(data.hedging.frequency),
    include_funding_rates: data.hedging.includeFundingRates,
    delta_threshold: data.hedging.deltaThreshold
      ? parseNumberValue(data.hedging.deltaThresholdValue)
      : null,
    delta_threshold_upper: data.hedging.deltaThresholdUpper
      ? parseNumberValue(data.hedging.deltaThresholdUpperValue)
      : null,
    delta_threshold_lower: data.hedging.deltaThresholdLower
      ? parseNumberValue(data.hedging.deltaThresholdLowerValue)
      : null,
    delta_target: data.hedging.deltaTarget ? parseNumberValue(data.hedging.deltaTargetValue) : null,
    include_token_balance_in_delta: data.hedging.includeTokenBalanceInDelta,
  },
  portfolio: data.portfolio.instruments
    .filter(
      (instrument) =>
        instrument.tenor.trim() && instrument.notional.trim() && instrument.deltaOrStrike.trim(),
    )
    .map(mapInstrumentToApi),
  reporting: DEFAULT_REPORTING,
  ...(mapEntryConditionsToApi(data.entryConditions)
    ? { entry_conditions: mapEntryConditionsToApi(data.entryConditions) }
    : {}),
});

const normalizeStrategyData = (scenarios: BacktesterScenario[]): BacktesterStrategyData =>
  scenarios.reduce<BacktesterStrategyData>((acc, scenario, index) => {
    const strategyName =
      typeof scenario.stratlet?.name === 'string' && scenario.stratlet.name.trim()
        ? scenario.stratlet.name
        : `Strategy ${index + 1}`;

    acc[strategyName] = scenario;
    return acc;
  }, {});

export const BacktestingContext = createContext({} as IBacktestingProvider);

export const BacktestingProvider = ({ children }: IBacktestingProviderProps) => {
  const { isConnected, sendMessage, subscribeToChannel } = useContext(WebSocketContext);

  const initialTab = createDefaultBacktestTab('Backtest');
  const [backtestTabs, setBacktestTabs] = useState<BacktestTab[]>([initialTab]);
  const [activeBacktestId, setActiveBacktestId] = useState<string | null>(initialTab.id);

  const strategyDraftsRef = useRef<Record<string, Record<string, StrategyFormData>>>({});
  const strategyFormsRef = useRef<Record<string, Record<string, StrategyFormRegistryEntry>>>({});
  const backtestHeaderRef = useRef<Record<string, BacktestHeaderRegistryEntry>>({});
  const strategyNameCountersRef = useRef<Record<string, number>>({});
  const pendingSavedConfigLoadRef = useRef<{ backtestId: string; name: string } | null>(null);
  // A rename/duplicate is a fetch-then-save-as-new (+ delete-old for rename),
  // since the config API has no native rename. This ref carries the op across
  // the async fetch so the fetchedConfig effect can complete it WITHOUT loading
  // the config into the working tab (that's what pendingSavedConfigLoadRef is).
  const pendingSavedConfigCopyRef = useRef<{
    type: 'rename' | 'duplicate';
    sourceName: string;
    newName: string;
  } | null>(null);
  const pendingSaveTypeRef = useRef<'save' | 'update'>('save');
  const [activeSavedConfigName, setActiveSavedConfigName] = useState<string | null>(null);

  const {
    listConfigs,
    fetchConfig,
    saveConfig,
    deleteConfig,
    configs,
    isListing,
    isFetching,
    fetchedConfig,
    isSaving,
    isDeleting,
    deletedConfigName: deletedSavedConfigName,
  } = useBacktesterConfigs();

  const setStoredStrategyDraft = useCallback(
    (backtestId: string, strategyId: string, draft: Partial<StrategyFormData>) => {
      if (!strategyDraftsRef.current[backtestId]) {
        strategyDraftsRef.current[backtestId] = {};
      }
      strategyDraftsRef.current[backtestId][strategyId] = cloneBacktestingValue(
        buildStrategyDraft(draft),
      );
    },
    [],
  );

  const getStoredStrategyDraft = useCallback((backtestId: string, strategyId: string) => {
    return strategyDraftsRef.current[backtestId]?.[strategyId];
  }, []);

  const syncStrategyNameValidation = useCallback(
    (backtestId: string, strategies: StrategyTab[]) => {
      const entries = strategyFormsRef.current[backtestId] ?? {};
      Object.values(entries).forEach((entry) => entry.clearNameError());

      const duplicateIds = getDuplicateStrategyIds(
        strategies.map((strategy) => ({
          id: strategy.id,
          name: strategyDraftsRef.current[backtestId]?.[strategy.id]?.name ?? strategy.label,
        })),
      );

      duplicateIds.forEach((strategyId) => {
        entries[strategyId]?.setNameError('Strategy names must be unique');
      });

      return duplicateIds;
    },
    [],
  );

  const getNextStrategyLabel = useCallback((backtestId: string) => {
    const nextSequence = (strategyNameCountersRef.current[backtestId] ?? 0) + 1;
    strategyNameCountersRef.current[backtestId] = nextSequence;
    return formatTradeStrategyLabel(nextSequence);
  }, []);

  const applySavedConfigToBacktest = useCallback(
    (backtestId: string, result: FetchBacktesterConfigResult) => {
      const payload = result.config ?? [];
      const nextDrafts = payload.stratlets.reduce<Record<string, StrategyFormData>>(
        (acc, stratlet) => {
          const strategyId = generateStrategyId();
          acc[strategyId] = mapStratletToFormData(stratlet);
          return acc;
        },
        {},
      );

      const nextStrategies = Object.entries(nextDrafts).map(([strategyId, draft]) => ({
        id: strategyId,
        label: draft.name.trim() || 'Trade Strategy',
      }));

      const firstToken = payload.stratlets[0]?.account.token;
      const nextGlobalConfig: BacktestGlobalConfig = {
        token: firstToken || 'BTC',
        backtestPeriod: {
          start: parseBacktesterDateString(payload.market_data.t_0),
          end: parseBacktesterDateString(payload.market_data.t_1),
        },
        frequency: payload.market_data.frequency ?? null,
      };

      strategyDraftsRef.current[backtestId] = nextDrafts;
      delete strategyFormsRef.current[backtestId];
      strategyNameCountersRef.current[backtestId] = nextStrategies.length;

      setBacktestTabs((prev) =>
        prev.map((tab) =>
          tab.id === backtestId
            ? {
                ...tab,
                globalConfig: nextGlobalConfig,
                headerErrors: {},
                strategies: nextStrategies,
                activeStrategyId: nextStrategies[0]?.id ?? null,
                execution: createDefaultBacktestExecutionState(),
              }
            : tab,
        ),
      );
    },
    [],
  );

  const listSavedConfigs = useCallback(
    (page = 1, pageSize = 10) => {
      listConfigs(page, pageSize);
    },
    [listConfigs],
  );

  const loadSavedConfig = useCallback(
    (name: string) => {
      if (!activeBacktestId) return;

      pendingSavedConfigLoadRef.current = {
        backtestId: activeBacktestId,
        name,
      };
      fetchConfig(name);
    },
    [activeBacktestId, fetchConfig],
  );

  const deleteSavedConfig = useCallback(
    (name: string) => {
      deleteConfig(name, {
        onSuccess: toastConfigDeleted,
        onError: toastConfigDeleteError,
      });
    },
    [deleteConfig],
  );

  const saveBacktestConfig = useCallback(
    (name?: string) => {
      const activeBacktest = backtestTabs.find((t) => t.id === activeBacktestId);
      if (!activeBacktest || !activeBacktestId) return;

      const strategyFormsData: StrategyFormData[] = activeBacktest.strategies.map((strategy) => {
        const entry = strategyFormsRef.current[activeBacktestId]?.[strategy.id];
        if (entry) {
          const values = entry.getValues();
          setStoredStrategyDraft(activeBacktestId, strategy.id, values);
          return values;
        }
        return (
          strategyDraftsRef.current[activeBacktestId]?.[strategy.id] ??
          buildStrategyDraft({ name: strategy.label })
        );
      });

      const payload = {
        market_data: {
          t_0: formatBacktesterDate(activeBacktest.globalConfig.backtestPeriod.start),
          t_1: formatBacktesterDate(activeBacktest.globalConfig.backtestPeriod.end),
          frequency: activeBacktest.globalConfig.frequency ?? 0,
        },
        stratlets: strategyFormsData.map(mapStrategyFormToApi),
      };

      const resolvedName =
        name?.trim() ||
        `Backtest Config - ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`;

      const isUpdate = pendingSaveTypeRef.current === 'update';
      pendingSaveTypeRef.current = 'save';

      saveConfig(resolvedName, payload, {
        onSuccess: () => {
          setActiveSavedConfigName(resolvedName);
          if (isUpdate) {
            toastConfigUpdated();
          } else {
            toastConfigSaved();
          }
        },
        onError: () => {
          if (isUpdate) {
            toastConfigUpdateError();
          } else {
            toastConfigSaveError();
          }
        },
      });
    },
    [activeBacktestId, backtestTabs, saveConfig, setStoredStrategyDraft],
  );

  const updateBacktestConfig = useCallback(() => {
    if (!activeSavedConfigName) return;
    pendingSaveTypeRef.current = 'update';
    saveBacktestConfig(activeSavedConfigName);
  }, [activeSavedConfigName, saveBacktestConfig]);

  // Rename a library entry: fetch its config, re-save under the new name, then
  // delete the old — the config API is name-keyed with no native rename. The
  // fetchedConfig effect completes the op (see pendingSavedConfigCopyRef).
  const renameSavedConfig = useCallback(
    (oldName: string, newName: string) => {
      const resolved = newName.trim();
      if (!resolved || resolved === oldName) return;
      pendingSavedConfigCopyRef.current = {
        type: 'rename',
        sourceName: oldName,
        newName: resolved,
      };
      fetchConfig(oldName);
    },
    [fetchConfig],
  );

  // Duplicate a library entry: fetch its config and save it under a new name,
  // leaving the original intact.
  const duplicateSavedConfig = useCallback(
    (sourceName: string, newName: string) => {
      const resolved = newName.trim() || `${sourceName} (copy)`;
      pendingSavedConfigCopyRef.current = {
        type: 'duplicate',
        sourceName,
        newName: resolved,
      };
      fetchConfig(sourceName);
    },
    [fetchConfig],
  );

  useEffect(() => {
    if (!fetchedConfig) return;

    // Rename/duplicate completion takes priority over a load: the copy op has
    // its own ref and must NOT apply the fetched config to the working tab.
    const copyOp = pendingSavedConfigCopyRef.current;
    if (copyOp) {
      if (fetchedConfig.name !== copyOp.sourceName) return;
      pendingSavedConfigCopyRef.current = null;

      saveConfig(copyOp.newName, fetchedConfig.config, {
        onSuccess: () => {
          if (copyOp.type === 'rename') {
            deleteConfig(copyOp.sourceName, {
              onSuccess: () => {
                if (activeSavedConfigName === copyOp.sourceName) {
                  setActiveSavedConfigName(copyOp.newName);
                }
                listSavedConfigs();
                toastConfigUpdated();
              },
              onError: toastConfigDeleteError,
            });
          } else {
            listSavedConfigs();
            toastConfigSaved();
          }
        },
        onError: toastConfigSaveError,
      });
      return;
    }

    if (!pendingSavedConfigLoadRef.current) return;
    if (fetchedConfig.name !== pendingSavedConfigLoadRef.current.name) return;

    applySavedConfigToBacktest(pendingSavedConfigLoadRef.current.backtestId, fetchedConfig);
    setActiveSavedConfigName(fetchedConfig.name);
    pendingSavedConfigLoadRef.current = null;
  }, [
    activeSavedConfigName,
    applySavedConfigToBacktest,
    deleteConfig,
    fetchedConfig,
    listSavedConfigs,
    saveConfig,
  ]);

  const addBacktestTab = useCallback(() => {
    setBacktestTabs((prev) => {
      const label = generateBacktestLabel(prev);
      const newTab = createDefaultBacktestTab(label);
      strategyNameCountersRef.current[newTab.id] = 0;
      setActiveBacktestId(newTab.id);
      return [...prev, newTab];
    });
  }, [setActiveBacktestId]);

  const renameBacktestTab = useCallback((id: string, newLabel: string) => {
    setBacktestTabs((prev) =>
      prev.map((tab) => (tab.id === id ? { ...tab, label: newLabel } : tab)),
    );
  }, []);

  const duplicateBacktestTab = useCallback(
    (id: string) => {
      setBacktestTabs((prev) => {
        const source = prev.find((t) => t.id === id);
        if (!source) return prev;

        const newId = generateBacktestId();
        const label = generateBacktestLabel(prev);
        const strategyIdMap = source.strategies.reduce<Record<string, string>>((acc, strategy) => {
          acc[strategy.id] = generateStrategyId();
          return acc;
        }, {});
        const newStrategies: StrategyTab[] = source.strategies.map((strategy) => ({
          ...strategy,
          id: strategyIdMap[strategy.id],
        }));

        const newTab: BacktestTab = {
          ...source,
          id: newId,
          label,
          globalConfig: cloneBacktestingValue(source.globalConfig),
          headerErrors: {},
          strategies: newStrategies,
          activeStrategyId: newStrategies.length > 0 ? newStrategies[0].id : null,
          execution: createDefaultBacktestExecutionState(),
        };

        strategyDraftsRef.current[newId] = source.strategies.reduce<
          Record<string, StrategyFormData>
        >((acc, strategy) => {
          const sourceDraft = getStoredStrategyDraft(id, strategy.id);
          if (sourceDraft) {
            acc[strategyIdMap[strategy.id]] = cloneBacktestingValue(sourceDraft);
          }
          return acc;
        }, {});
        strategyNameCountersRef.current[newId] = strategyNameCountersRef.current[id] ?? 0;

        setActiveBacktestId(newId);
        return [...prev, newTab];
      });
    },
    [getStoredStrategyDraft],
  );

  const deleteBacktestTab = useCallback(
    (id: string) => {
      setBacktestTabs((prev) => {
        if (prev.length <= 1) return prev;

        const index = prev.findIndex((t) => t.id === id);
        const filtered = prev.filter((t) => t.id !== id);

        delete backtestHeaderRef.current[id];
        delete strategyFormsRef.current[id];
        delete strategyDraftsRef.current[id];
        delete strategyNameCountersRef.current[id];

        if (activeBacktestId === id) {
          const newActiveIndex = Math.max(0, index - 1);
          setActiveBacktestId(filtered[newActiveIndex]?.id ?? null);
        }

        return filtered;
      });
    },
    [activeBacktestId],
  );

  const updateGlobalConfig = useCallback(
    (backtestId: string, config: Partial<BacktestGlobalConfig>) => {
      setBacktestTabs((prev) =>
        prev.map((tab) =>
          tab.id === backtestId
            ? {
                ...tab,
                globalConfig: { ...tab.globalConfig, ...config },
              }
            : tab,
        ),
      );
    },
    [],
  );

  const addStrategy = useCallback(
    (backtestId: string, label?: string, defaults?: Partial<StrategyFormData>) => {
      const newStrategyId = generateStrategyId();
      const fallbackLabel = label?.trim() || getNextStrategyLabel(backtestId);
      const nextDraft = buildStrategyDraft({
        ...defaults,
        name: defaults?.name?.trim() ? defaults.name : fallbackLabel,
      });
      setStoredStrategyDraft(backtestId, newStrategyId, nextDraft);

      let nextStrategies: StrategyTab[] = [];
      setBacktestTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== backtestId) return tab;

          const newStrategy: StrategyTab = { id: newStrategyId, label: nextDraft.name };
          nextStrategies = [...tab.strategies, newStrategy];
          return {
            ...tab,
            strategies: nextStrategies,
            activeStrategyId: newStrategyId,
          };
        }),
      );

      if (nextStrategies.length > 0) {
        syncStrategyNameValidation(backtestId, nextStrategies);
      }
    },
    [getNextStrategyLabel, setStoredStrategyDraft, syncStrategyNameValidation],
  );

  const duplicateStrategy = useCallback(
    (backtestId: string, strategyId: string, data: StrategyFormData) => {
      const newStrategyId = generateStrategyId();

      let nextStrategies: StrategyTab[] = [];
      setBacktestTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== backtestId) return tab;

          const sourceIndex = tab.strategies.findIndex((strategy) => strategy.id === strategyId);
          if (sourceIndex < 0) return tab;

          const sourceStrategy = tab.strategies[sourceIndex];
          const existingNames = tab.strategies.map(
            (strategy) =>
              strategyDraftsRef.current[backtestId]?.[strategy.id]?.name ?? strategy.label,
          );
          const copyName = generateCopyStrategyName(
            data.name || sourceStrategy.label,
            existingNames,
          );
          const nextDraft = buildStrategyDraft({ ...data, name: copyName });
          setStoredStrategyDraft(backtestId, newStrategyId, nextDraft);

          const duplicatedStrategy: StrategyTab = {
            id: newStrategyId,
            label: copyName,
          };

          nextStrategies = [...tab.strategies];
          nextStrategies.splice(sourceIndex + 1, 0, duplicatedStrategy);

          return {
            ...tab,
            strategies: nextStrategies,
            activeStrategyId: newStrategyId,
          };
        }),
      );

      if (nextStrategies.length > 0) {
        syncStrategyNameValidation(backtestId, nextStrategies);
      }
    },
    [setStoredStrategyDraft, syncStrategyNameValidation],
  );

  const renameStrategy = useCallback(
    (backtestId: string, strategyId: string, newLabel: string) => {
      const currentDraft = getStoredStrategyDraft(backtestId, strategyId);
      setStoredStrategyDraft(backtestId, strategyId, {
        ...(currentDraft ?? buildStrategyDraft()),
        name: newLabel,
      });

      let nextStrategies: StrategyTab[] = [];
      setBacktestTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== backtestId) return tab;
          nextStrategies = tab.strategies.map((strategy) =>
            strategy.id === strategyId ? { ...strategy, label: newLabel } : strategy,
          );
          return {
            ...tab,
            strategies: nextStrategies,
          };
        }),
      );
      syncStrategyNameValidation(backtestId, nextStrategies);
    },
    [getStoredStrategyDraft, setStoredStrategyDraft, syncStrategyNameValidation],
  );

  const deleteStrategy = useCallback(
    (backtestId: string, strategyId: string) => {
      let nextStrategies: StrategyTab[] | null = null;
      setBacktestTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== backtestId) return tab;

          // Invariant: a study always keeps at least one backtest. Deleting the
          // last one would strand the shell in a zero-backtest dead-end (the
          // very state the sidebar/shell consolidation removed). The UI hides
          // the delete affordance at one row; this is the correctness backstop.
          if (tab.strategies.length <= 1) return tab;

          delete strategyFormsRef.current[backtestId]?.[strategyId];
          delete strategyDraftsRef.current[backtestId]?.[strategyId];

          const index = tab.strategies.findIndex((s) => s.id === strategyId);
          const filtered = tab.strategies.filter((s) => s.id !== strategyId);
          nextStrategies = filtered;
          let newActiveId = tab.activeStrategyId;

          if (tab.activeStrategyId === strategyId) {
            const newIndex = Math.max(0, index - 1);
            newActiveId = filtered[newIndex]?.id ?? null;
          }

          return { ...tab, strategies: filtered, activeStrategyId: newActiveId };
        }),
      );
      if (nextStrategies) syncStrategyNameValidation(backtestId, nextStrategies);
    },
    [syncStrategyNameValidation],
  );

  const clearAllStrategiesForBacktest = useCallback((backtestId: string) => {
    setBacktestTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== backtestId) return tab;

        delete strategyFormsRef.current[backtestId];
        delete strategyDraftsRef.current[backtestId];
        strategyNameCountersRef.current[backtestId] = 0;

        return {
          ...tab,
          strategies: [],
          activeStrategyId: null,
          headerErrors: {},
          globalConfig: createDefaultGlobalConfig(),
          execution: createDefaultBacktestExecutionState(),
        };
      }),
    );
  }, []);

  const setActiveStrategy = useCallback((backtestId: string, strategyId: string) => {
    setBacktestTabs((prev) =>
      prev.map((tab) => (tab.id === backtestId ? { ...tab, activeStrategyId: strategyId } : tab)),
    );
  }, []);

  const addStrategyFromTemplate = useCallback(
    (template: StrategyTemplate) => {
      if (!activeBacktestId) return;
      addStrategy(activeBacktestId, template.label, template.defaults);
    },
    [activeBacktestId, addStrategy],
  );

  // Reset the single working study to a fresh, unsaved draft, seeded with one
  // backtest (blank, or from a Model template). The sidebar is the persistent
  // library, so there is only ever one working copy — this replaces it rather
  // than accumulating tabs. It always has at least one backtest so it lands
  // straight in the shell on the Specification tab (no zero-strategy dead-end),
  // and activeSavedConfigName is cleared so it reads as an unsaved draft until
  // it's Saved (or run).
  const startNewBacktest = useCallback(
    (template?: StrategyTemplate) => {
      const newTab = createDefaultBacktestTab('Untitled study');
      strategyNameCountersRef.current[newTab.id] = 0;

      const strategyId = generateStrategyId();
      const draft = buildStrategyDraft(
        template
          ? {
              ...template.defaults,
              name: template.defaults.name?.trim() ? template.defaults.name : template.label,
            }
          : { name: getNextStrategyLabel(newTab.id) },
      );
      setStoredStrategyDraft(newTab.id, strategyId, draft);
      newTab.strategies = [{ id: strategyId, label: draft.name }];
      newTab.activeStrategyId = strategyId;

      setBacktestTabs([newTab]);
      setActiveBacktestId(newTab.id);
      setActiveSavedConfigName(null);
    },
    [getNextStrategyLabel, setStoredStrategyDraft],
  );

  const getActiveBacktest = useCallback((): BacktestTab | undefined => {
    return backtestTabs.find((t) => t.id === activeBacktestId);
  }, [backtestTabs, activeBacktestId]);

  const getStrategyDefaults = useCallback(
    (backtestId: string, strategyId: string): Partial<StrategyFormData> => {
      return getStoredStrategyDraft(backtestId, strategyId) ?? buildStrategyDraft();
    },
    [getStoredStrategyDraft],
  );

  const updateStrategyDraft = useCallback(
    (backtestId: string, strategyId: string, draft: StrategyFormData) => {
      setStoredStrategyDraft(backtestId, strategyId, buildStrategyDraft(draft));
    },
    [setStoredStrategyDraft],
  );

  const syncStrategyLabel = useCallback(
    (backtestId: string, strategyId: string, name: string) => {
      let nextStrategies: StrategyTab[] = [];
      setBacktestTabs((prev) => {
        const tab = prev.find((t) => t.id === backtestId);
        if (!tab) return prev;

        const strategy = tab.strategies.find((s) => s.id === strategyId);
        if (!strategy || strategy.label === name) return prev;

        nextStrategies = tab.strategies.map((s) =>
          s.id === strategyId ? { ...s, label: name } : s,
        );
        return prev.map((t) => (t.id === backtestId ? { ...t, strategies: nextStrategies } : t));
      });

      if (nextStrategies.length > 0) {
        syncStrategyNameValidation(backtestId, nextStrategies);
      }
    },
    [syncStrategyNameValidation],
  );

  const registerBacktestHeader = useCallback(
    (backtestId: string, entry: BacktestHeaderRegistryEntry) => {
      backtestHeaderRef.current[backtestId] = entry;
    },
    [],
  );

  const unregisterBacktestHeader = useCallback((backtestId: string) => {
    delete backtestHeaderRef.current[backtestId];
  }, []);

  const registerStrategyForm = useCallback(
    (strategyId: string, entry: StrategyFormRegistryEntry) => {
      if (!strategyFormsRef.current[entry.backtestId]) {
        strategyFormsRef.current[entry.backtestId] = {};
      }

      strategyFormsRef.current[entry.backtestId][strategyId] = entry;
    },
    [],
  );

  const unregisterStrategyForm = useCallback((strategyId: string) => {
    Object.values(strategyFormsRef.current).forEach((entries) => {
      delete entries[strategyId];
    });
  }, []);

  const setBacktestHeaderErrors = useCallback(
    (backtestId: string, errors: BacktestTab['headerErrors']) => {
      setBacktestTabs((prev) =>
        prev.map((tab) =>
          tab.id === backtestId
            ? {
                ...tab,
                headerErrors: errors,
              }
            : tab,
        ),
      );
    },
    [],
  );

  const clearBacktestHeaderError = useCallback((backtestId: string, field: BacktestHeaderField) => {
    setBacktestTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== backtestId || !tab.headerErrors[field]) return tab;

        const nextErrors = { ...tab.headerErrors };
        delete nextErrors[field];

        return {
          ...tab,
          headerErrors: nextErrors,
        };
      }),
    );
  }, []);

  const showBacktestForm = useCallback((backtestId: string) => {
    setBacktestTabs((prev) =>
      prev.map((tab) =>
        tab.id === backtestId
          ? {
              ...tab,
              execution: {
                ...tab.execution,
                viewMode: 'form',
              },
            }
          : tab,
      ),
    );
  }, []);

  const setActiveBacktestResultTab = useCallback(
    (backtestId: string, tabKey: BacktestTab['execution']['activeResultTab']) => {
      setBacktestTabs((prev) =>
        prev.map((tab) =>
          tab.id === backtestId
            ? {
                ...tab,
                execution: {
                  ...tab.execution,
                  activeResultTab: tabKey,
                },
              }
            : tab,
        ),
      );
    },
    [],
  );

  const runBacktest = useCallback(async () => {
    const activeBacktest = backtestTabs.find((t) => t.id === activeBacktestId);
    if (!activeBacktest || !activeBacktestId || !isConnected) return false;

    const entries = activeBacktest.strategies.map(
      (strategy) => strategyFormsRef.current[activeBacktestId]?.[strategy.id],
    );
    const isFormMode = activeBacktest.execution.viewMode === 'form';

    if (!SKIP_VALIDATION_FOR_DEMO && isFormMode) {
      // Validate against the mounted forms/header when present, else fall back
      // to the stored globalConfig / strategy drafts — the draft is the source
      // of truth, the mounted forms are just an editing surface. This lets a
      // run triggered from the run-prompt (on a result tab, with Specification
      // unmounted) succeed instead of bailing on "not registered".
      const headerEntry = backtestHeaderRef.current[activeBacktestId];
      let headerValid: boolean;
      if (headerEntry) {
        headerValid = await headerEntry.trigger();
      } else {
        const headerErrors = validateGlobalConfig(activeBacktest.globalConfig);
        setBacktestHeaderErrors(activeBacktestId, headerErrors);
        headerValid = Object.keys(headerErrors).length === 0;
      }

      const strategyResults = await Promise.all(
        activeBacktest.strategies.map((strategy, index) => {
          const entry = entries[index];
          if (entry) return entry.trigger();
          const draft =
            getStoredStrategyDraft(activeBacktestId, strategy.id) ??
            buildStrategyDraft({ name: strategy.label });
          return Promise.resolve(isStrategyDraftValid(draft));
        }),
      );

      const isValid = headerValid && strategyResults.every(Boolean);

      if (!isValid) {
        setBacktestTabs((prev) =>
          prev.map((tab) =>
            tab.id !== activeBacktestId
              ? tab
              : {
                  ...tab,
                  strategies: tab.strategies.map((s, i) => ({
                    ...s,
                    hasError: strategyResults[i] === false,
                  })),
                },
          ),
        );
        return false;
      }

      setBacktestTabs((prev) =>
        prev.map((tab) =>
          tab.id !== activeBacktestId
            ? tab
            : {
                ...tab,
                strategies: tab.strategies.map((s) => ({ ...s, hasError: false })),
              },
        ),
      );
    }

    const strategyFormsData: StrategyFormData[] = activeBacktest.strategies.map(
      (strategy, index) => {
        const entry = entries[index];
        if (entry) {
          const values = entry.getValues();
          setStoredStrategyDraft(activeBacktestId, strategy.id, values);
          return values;
        }

        const storedDraft = getStoredStrategyDraft(activeBacktestId, strategy.id);
        if (storedDraft) return storedDraft;

        return buildStrategyDraft({ name: strategy.label });
      },
    );

    const duplicateIds = getDuplicateStrategyIds(
      activeBacktest.strategies.map((strategy, index) => ({
        id: strategy.id,
        name: strategyFormsData[index]?.name ?? strategy.label,
      })),
    );

    syncStrategyNameValidation(activeBacktestId, activeBacktest.strategies);

    if (duplicateIds.length > 0) {
      return false;
    }

    const payload = {
      market_data: {
        t_0: formatBacktesterDate(activeBacktest.globalConfig.backtestPeriod.start),
        t_1: formatBacktesterDate(activeBacktest.globalConfig.backtestPeriod.end),
        frequency: activeBacktest.globalConfig.frequency ?? 0,
      },
      stratlets: strategyFormsData.map(mapStrategyFormToApi),
    };

    // Auto-save-on-run: a run always persists the study to the library so it
    // survives without an explicit Save. Latest-state / overwrite — an existing
    // saved study updates in place; a draft becomes a saved entry (whose name
    // we resolve now and pin via activeSavedConfigName so later runs overwrite
    // the same entry rather than spawning "(2)" copies). We save exactly the
    // payload that ran, so the saved study is reproducible.
    const autoSaveName =
      activeSavedConfigName ??
      resolveAutoSaveName(
        activeBacktest.label,
        payload.stratlets[0]?.name,
        new Set((configs ?? []).map((c) => c.name)),
      );

    const request = buildRunBacktestRequest(payload);

    setBacktestTabs((prev) =>
      prev.map((tab) =>
        tab.id === activeBacktestId
          ? {
              ...tab,
              execution: {
                ...tab.execution,
                status: 'loading',
                executedAt: null,
                rawResult: null,
                strategyData: null,
                strategyNames: [],
                // Capture the exact JSON-RPC request sent, for the Request tab.
                lastRunRequest: request,
              },
            }
          : tab,
      ),
    );

    sendMessage(JSON.stringify(request));
    subscribeToChannel(request.id, (response: BacktesterRunResponse) => {
      if (!response.result || response.error) {
        toastBacktestRunError();
        setBacktestTabs((prev) =>
          prev.map((tab) =>
            tab.id === activeBacktestId
              ? {
                  ...tab,
                  execution: {
                    ...tab.execution,
                    status: 'idle',
                    viewMode: 'form',
                    rawResult: null,
                    strategyData: null,
                    strategyNames: [],
                  },
                }
              : tab,
          ),
        );
        return;
      }

      const executedAt = new Date().toISOString();
      const strategyData = normalizeStrategyData(response.result.scenarios);
      const strategyNames = Object.keys(strategyData);

      // Silent auto-save into the library — the study appearing/updating in
      // "Saved backtests" is the feedback, no toast on every run. Refresh the
      // list and pin the name so this study is now a saved entry, not a draft.
      saveConfig(autoSaveName, payload, {
        onSuccess: () => {
          setActiveSavedConfigName(autoSaveName);
          listSavedConfigs();
        },
      });

      setBacktestTabs((prev) =>
        prev.map((tab) =>
          tab.id === activeBacktestId
            ? {
                ...tab,
                execution: {
                  ...tab.execution,
                  status: 'success',
                  viewMode: 'result',
                  // Always land on Performance/results after a run, regardless
                  // of which tab it was launched from (e.g. Specification).
                  // 'overview' is the internal key for the "Performance" tab
                  // (kept for backwards-compat; see RESULT_TABS in the shell).
                  activeResultTab: 'overview',
                  executedAt,
                  rawResult: response.result ?? null,
                  strategyData,
                  strategyNames,
                },
              }
            : tab,
        ),
      );
    });

    return true;
  }, [
    activeBacktestId,
    activeSavedConfigName,
    backtestTabs,
    configs,
    getStoredStrategyDraft,
    isConnected,
    listSavedConfigs,
    saveConfig,
    sendMessage,
    setBacktestHeaderErrors,
    setStoredStrategyDraft,
    subscribeToChannel,
    syncStrategyNameValidation,
  ]);

  // Per-variant Run — runs ONE stratlet and merges just that variant's result
  // into the study's result set, leaving sibling variants' results untouched.
  // Mirrors runBacktest's success path (validate -> single-stratlet payload ->
  // sendMessage/subscribe -> normalizeStrategyData), but merges instead of
  // replacing and flags per-variant loading via runningStrategyIds rather than
  // the study-wide status.
  const runStrategy = useCallback(
    async (backtestId: string, strategyId: string) => {
      const backtest = backtestTabs.find((t) => t.id === backtestId);
      if (!backtest || !isConnected) return false;
      const strategy = backtest.strategies.find((s) => s.id === strategyId);
      if (!strategy) return false;

      const formEntry = strategyFormsRef.current[backtestId]?.[strategyId];

      // Validate the header (study-level, shared) and THIS strategy only. Reuse
      // the mounted form/header when present, else fall back to the stored
      // drafts / globalConfig — same source-of-truth precedence as runBacktest.
      if (!SKIP_VALIDATION_FOR_DEMO) {
        const headerEntry = backtestHeaderRef.current[backtestId];
        let headerValid: boolean;
        if (headerEntry) {
          headerValid = await headerEntry.trigger();
        } else {
          const headerErrors = validateGlobalConfig(backtest.globalConfig);
          setBacktestHeaderErrors(backtestId, headerErrors);
          headerValid = Object.keys(headerErrors).length === 0;
        }

        let strategyValid: boolean;
        if (formEntry) {
          strategyValid = await formEntry.trigger();
        } else {
          const draft =
            getStoredStrategyDraft(backtestId, strategyId) ??
            buildStrategyDraft({ name: strategy.label });
          strategyValid = isStrategyDraftValid(draft);
        }

        if (!headerValid || !strategyValid) {
          setBacktestTabs((prev) =>
            prev.map((tab) =>
              tab.id !== backtestId
                ? tab
                : {
                    ...tab,
                    strategies: tab.strategies.map((s) =>
                      s.id === strategyId ? { ...s, hasError: !strategyValid } : s,
                    ),
                  },
            ),
          );
          return false;
        }
      }

      // Latest values for the run-target variant (persist as its draft), plus
      // the full-study drafts for auto-save — a per-variant run must not shrink
      // the saved study to a single stratlet.
      const runDraft = (() => {
        if (formEntry) {
          const values = formEntry.getValues();
          setStoredStrategyDraft(backtestId, strategyId, values);
          return values;
        }
        return (
          getStoredStrategyDraft(backtestId, strategyId) ??
          buildStrategyDraft({ name: strategy.label })
        );
      })();

      const fullStudyDrafts: StrategyFormData[] = backtest.strategies.map((s) => {
        if (s.id === strategyId) return runDraft;
        const entry = strategyFormsRef.current[backtestId]?.[s.id];
        if (entry) return entry.getValues();
        return getStoredStrategyDraft(backtestId, s.id) ?? buildStrategyDraft({ name: s.label });
      });

      // The run-time name keys the result set, so a variant sharing a name with
      // a sibling would merge onto it. Reuse the study-wide uniqueness guard.
      const duplicateIds = getDuplicateStrategyIds(
        backtest.strategies.map((s, index) => ({
          id: s.id,
          name: fullStudyDrafts[index]?.name ?? s.label,
        })),
      );
      syncStrategyNameValidation(backtestId, backtest.strategies);
      if (duplicateIds.length > 0) return false;

      const marketData = {
        t_0: formatBacktesterDate(backtest.globalConfig.backtestPeriod.start),
        t_1: formatBacktesterDate(backtest.globalConfig.backtestPeriod.end),
        frequency: backtest.globalConfig.frequency ?? 0,
      };
      const runPayload = { market_data: marketData, stratlets: [mapStrategyFormToApi(runDraft)] };
      const autoSavePayload = {
        market_data: marketData,
        stratlets: fullStudyDrafts.map(mapStrategyFormToApi),
      };

      const autoSaveName =
        activeSavedConfigName ??
        resolveAutoSaveName(
          backtest.label,
          autoSavePayload.stratlets[0]?.name,
          new Set((configs ?? []).map((c) => c.name)),
        );

      const request = buildRunBacktestRequest(
        runPayload,
        `${BACKTESTER_CONFIG_KEYS.RUN}_${strategyId}`,
      );

      // Flag ONLY this strategy as running. No study-wide status change.
      setBacktestTabs((prev) =>
        prev.map((tab) =>
          tab.id !== backtestId
            ? tab
            : {
                ...tab,
                strategies: tab.strategies.map((s) =>
                  s.id === strategyId ? { ...s, hasError: false } : s,
                ),
                execution: {
                  ...tab.execution,
                  runningStrategyIds: tab.execution.runningStrategyIds.includes(strategyId)
                    ? tab.execution.runningStrategyIds
                    : [...tab.execution.runningStrategyIds, strategyId],
                  // Capture the exact single-variant request for the Request tab.
                  lastRunRequest: request,
                },
              },
        ),
      );

      const clearRunning = () =>
        setBacktestTabs((prev) =>
          prev.map((tab) =>
            tab.id !== backtestId
              ? tab
              : {
                  ...tab,
                  execution: {
                    ...tab.execution,
                    runningStrategyIds: tab.execution.runningStrategyIds.filter(
                      (id) => id !== strategyId,
                    ),
                  },
                },
          ),
        );

      sendMessage(JSON.stringify(request));
      subscribeToChannel(request.id, (response: BacktesterRunResponse) => {
        if (!response.result || response.error) {
          toastBacktestRunError();
          clearRunning();
          return;
        }

        const executedAt = new Date().toISOString();
        // Merge ONLY this variant's scenario, keyed by its run-time name; every
        // sibling variant's existing result is preserved.
        const merged = normalizeStrategyData(response.result.scenarios);

        // Auto-save the FULL study (overwrite) — same silent latest-state save
        // runBacktest does, so per-variant runs keep the library in step.
        saveConfig(autoSaveName, autoSavePayload, {
          onSuccess: () => {
            setActiveSavedConfigName(autoSaveName);
            listSavedConfigs();
          },
        });

        setBacktestTabs((prev) =>
          prev.map((tab) => {
            if (tab.id !== backtestId) return tab;
            const nextData = { ...(tab.execution.strategyData ?? {}), ...merged };
            return {
              ...tab,
              execution: {
                ...tab.execution,
                // Don't disturb a full run in flight; only promote a never-run
                // study to 'success' now that one variant has results.
                status: tab.execution.status === 'idle' ? 'success' : tab.execution.status,
                executedAt,
                strategyData: nextData,
                strategyNames: Object.keys(nextData),
                runningStrategyIds: tab.execution.runningStrategyIds.filter(
                  (id) => id !== strategyId,
                ),
              },
            };
          }),
        );
      });

      return true;
    },
    [
      activeSavedConfigName,
      backtestTabs,
      configs,
      getStoredStrategyDraft,
      isConnected,
      listSavedConfigs,
      saveConfig,
      sendMessage,
      setBacktestHeaderErrors,
      setStoredStrategyDraft,
      subscribeToChannel,
      syncStrategyNameValidation,
    ],
  );

  const contextValue = useMemo(
    () => ({
      backtestTabs,
      activeBacktestId,
      setActiveBacktestId,
      addBacktestTab,
      renameBacktestTab,
      duplicateBacktestTab,
      deleteBacktestTab,
      updateGlobalConfig,
      addStrategy,
      duplicateStrategy,
      renameStrategy,
      deleteStrategy,
      clearAllStrategiesForBacktest,
      setActiveStrategy,
      updateStrategyDraft,
      syncStrategyLabel,
      addStrategyFromTemplate,
      startNewBacktest,
      listSavedConfigs,
      loadSavedConfig,
      deleteSavedConfig,
      renameSavedConfig,
      duplicateSavedConfig,
      saveBacktestConfig,
      updateBacktestConfig,
      savedConfigs: configs,
      isListingSavedConfigs: isListing,
      isLoadingSavedConfig: isFetching,
      isDeletingSavedConfig: isDeleting,
      isSavingConfig: isSaving,
      deletedSavedConfigName,
      activeSavedConfigName,
      getActiveBacktest,
      getStrategyDefaults,
      registerBacktestHeader,
      unregisterBacktestHeader,
      registerStrategyForm,
      unregisterStrategyForm,
      setBacktestHeaderErrors,
      clearBacktestHeaderError,
      showBacktestForm,
      setActiveBacktestResultTab,
      runBacktest,
      runStrategy,
    }),
    [
      activeBacktestId,
      activeSavedConfigName,
      addBacktestTab,
      addStrategy,
      addStrategyFromTemplate,
      backtestTabs,
      clearAllStrategiesForBacktest,
      clearBacktestHeaderError,
      configs,
      deleteBacktestTab,
      deletedSavedConfigName,
      deleteSavedConfig,
      deleteStrategy,
      duplicateBacktestTab,
      duplicateSavedConfig,
      duplicateStrategy,
      getActiveBacktest,
      getStrategyDefaults,
      isDeleting,
      isFetching,
      isListing,
      isSaving,
      listSavedConfigs,
      loadSavedConfig,
      registerBacktestHeader,
      registerStrategyForm,
      renameBacktestTab,
      renameSavedConfig,
      renameStrategy,
      runBacktest,
      runStrategy,
      saveBacktestConfig,
      setActiveBacktestResultTab,
      setActiveStrategy,
      setBacktestHeaderErrors,
      showBacktestForm,
      startNewBacktest,
      syncStrategyLabel,
      unregisterBacktestHeader,
      unregisterStrategyForm,
      updateBacktestConfig,
      updateGlobalConfig,
      updateStrategyDraft,
    ],
  );

  return <BacktestingContext.Provider value={contextValue}>{children}</BacktestingContext.Provider>;
};
